import * as React from 'react';

import { Typography, useMediaQuery, useTheme } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import { ROUTES } from '@kamernet/core/Routing/routes';
import {
  ListingsSearchCategory,
  getMainListingsSearchCategory,
} from '@kamernet/core/Routing/routes/ListingsSearch';
import KNLogo from '@kamernet/icons/KN_Logo.svg';

import { Breadcrumbs as MuiBreadcrumbs } from '@kamernet/components/Breadcrumbs';
import {
  isCitySearch,
  isCountrySearch,
  isStreetSearch,
} from '@kamernet/modules/Search/utilities';

import { ListingsSearchContext } from '../../ListingsSearchContext';

import styles from './Breadcrumbs.module.css';

const SEARCH_CATEGORY_TO_STRING_EN: Record<ListingsSearchCategory, string> = {
  [ListingsSearchCategory.Empty]: '',
  [ListingsSearchCategory.Generic]: 'Properties',
  [ListingsSearchCategory.Room]: 'Rooms',
  [ListingsSearchCategory.Apartment]: 'Apartments',
  [ListingsSearchCategory.Studio]: 'Studios',
  [ListingsSearchCategory.Antisquat]: 'Anti-squats',
  [ListingsSearchCategory.StudentHousing]: 'Student accommodations',
  [ListingsSearchCategory.FurnishedApartment]: 'Furnished apartments',
  [ListingsSearchCategory.LongTermRental]: 'Long Term Rental',
  [ListingsSearchCategory.ShortTermRental]: 'Short Term Rentals',
};

const SEARCH_CATEGORY_TO_STRING_NL: Record<ListingsSearchCategory, string> = {
  [ListingsSearchCategory.Empty]: '',
  [ListingsSearchCategory.Generic]: 'Huurwoningen',
  [ListingsSearchCategory.Room]: 'Kamers',
  [ListingsSearchCategory.Apartment]: 'Appartementen',
  [ListingsSearchCategory.Studio]: 'Studios',
  [ListingsSearchCategory.Antisquat]: 'Anti-kraaks',
  [ListingsSearchCategory.StudentHousing]: 'Studentenwoningen',
  [ListingsSearchCategory.FurnishedApartment]: 'Gemeubileerde appartementen',
  [ListingsSearchCategory.LongTermRental]: 'Long Term Rental',
  [ListingsSearchCategory.ShortTermRental]: 'Short Term Rentals',
};

export const Breadcrumbs = () => {
  const { T, locale } = useIntl();
  const { allFilters } = ListingsSearchContext.useContext();
  const theme = useTheme();
  const isMDUp = useMediaQuery(theme.breakpoints.up('md'));
  const placeHolder = '...';

  const mainSearchCategory = getMainListingsSearchCategory(
    allFilters.searchCategories,
  );

  return (
    <section data-testid="breadcrumb-container">
      <MuiBreadcrumbs>
        <MuiBreadcrumbs.Link href={ROUTES.Home.getURL({}, locale)}>
          <KNLogo className={styles.icon} />
          {T('LBL_SMARTBANNER_TITLE')}
        </MuiBreadcrumbs.Link>

        {isCountrySearch(allFilters) && (
          <MuiBreadcrumbs.Link
            href={ROUTES.ListingsSearchCity.getURL(
              {
                searchCategories: allFilters.searchCategories,
              },
              locale,
            )}
          >
            {locale === Locale.en
              ? T(
                  '%_%',
                  'Netherlands',
                  SEARCH_CATEGORY_TO_STRING_EN[mainSearchCategory],
                )
              : T(
                  '%_%',
                  SEARCH_CATEGORY_TO_STRING_NL[mainSearchCategory],
                  'Nederland',
                )}
          </MuiBreadcrumbs.Link>
        )}
        {(isCitySearch(allFilters) || isStreetSearch(allFilters)) && (
          <MuiBreadcrumbs.Link
            href={ROUTES.ListingsSearchCity.getURL(
              {
                city: allFilters.citySlug,
                searchCategories: allFilters.searchCategories,
              },
              locale,
            )}
          >
            {locale === Locale.en
              ? T(
                  '%_%',
                  allFilters.cityName,
                  SEARCH_CATEGORY_TO_STRING_EN[mainSearchCategory],
                )
              : T(
                  '%_%',
                  SEARCH_CATEGORY_TO_STRING_NL[mainSearchCategory],
                  allFilters.cityName,
                )}
          </MuiBreadcrumbs.Link>
        )}
        {isStreetSearch(allFilters) && (
          <MuiBreadcrumbs.Text>
            {isMDUp ? (
              <NavLink.Anchor
                href={ROUTES.ListingsSearchCity.getURL(
                  {
                    city: allFilters.citySlug,
                    searchCategories: allFilters.searchCategories,
                    street: allFilters.streetSlug,
                  },
                  locale,
                )}
              >
                {locale === Locale.en
                  ? T(
                      '%_%',
                      allFilters.streetName,
                      SEARCH_CATEGORY_TO_STRING_EN[mainSearchCategory],
                    )
                  : T(
                      '%_%',
                      SEARCH_CATEGORY_TO_STRING_NL[mainSearchCategory],
                      allFilters.streetName,
                    )}
              </NavLink.Anchor>
            ) : (
              <Typography>{placeHolder}</Typography>
            )}
          </MuiBreadcrumbs.Text>
        )}
      </MuiBreadcrumbs>
    </section>
  );
};
